import { injectIntl } from "react-intl";
import { useState } from "react";

const ROW_SIZE_OPTIONS = [10, 20, 50, 100];

const Pagination = (props) => {
  const visiblePages = 5;
  const halfVisible = Math.floor(visiblePages / 2);
  const pageSize = props.pageSize ?? 20;
  const totalCount = props.totalCount ?? props.totalPages * pageSize;
  const hasItems = totalCount > 0 && props.totalPages > 0;
  const rangeStart = hasItems ? (props.currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = hasItems
    ? Math.min(props.currentPage * pageSize, totalCount)
    : 0;

  const [goToValue, setGoToValue] = useState("");
  const [goToError, setGoToError] = useState(false);

  const getPageNumbers = () => {
    if (props.totalPages <= 0) {
      return [];
    }

    const pages = [];
    const start = Math.max(1, props.currentPage - halfVisible);
    const end = Math.min(props.totalPages, props.currentPage + halfVisible);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (start > 1) {
      pages.unshift("...");
      pages.unshift(1);
    }

    if (end < props.totalPages) {
      pages.push("...");
      pages.push(props.totalPages);
    }

    return pages;
  };

  const handleGoToChange = (event) => {
    const value = event.target.value;

    if (value === "" || /^\d+$/.test(value)) {
      setGoToValue(value);
      setGoToError(false);
    }
  };

  const handleGoToSubmit = () => {
    const page = parseInt(goToValue, 10);

    if (
      !goToValue ||
      Number.isNaN(page) ||
      page <= 0 ||
      page > props.totalPages
    ) {
      setGoToError(true);
      return;
    }

    setGoToError(false);
    props.onPageChange(page);
    setGoToValue("");
  };

  const handleGoToKeyDown = (event) => {
    if (event.key === "Enter") {
      handleGoToSubmit();
    }
  };

  return (
    <div
      className={`Pagination${props.popup ? " Pagination--popup padding-0" : ""}`}
    >
      <div className="PaginationSide">
        <div className="PaginationLabel">
          {rangeStart}-{rangeEnd} of {totalCount}
        </div>
        <div className="PaginationRows">
          <div className="PaginationLabel">Rows:</div>
          <div className="PaginationRowSizeGroup">
            {ROW_SIZE_OPTIONS.map((size) => (
              <div
                key={size}
                className={`PaginationRowSizeOption${
                  pageSize === size ? " PaginationRowSizeOptionSelected" : ""
                }`}
                onClick={() => props.onPageSizeChange?.(size)}
              >
                {size}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="PaginationRight">
        <div className="PaginationPageGroup">
          <button
            type="button"
            className="PaginationNavButton"
            onClick={() =>
              props.currentPage > 1 && props.onPageChange(props.currentPage - 1)
            }
            disabled={props.currentPage <= 1 || props.totalPages <= 0}
            aria-label="Previous page"
          >
            <img alt="" src="/media/icons/chevronLeft.svg" />
          </button>
          {getPageNumbers().map((page, index) => (
            <div
              key={`${page}-${index}`}
              className={`PaginationPageOption${
                props.currentPage === page
                  ? " PaginationPageOptionSelected"
                  : ""
              }${page === "..." ? " PaginationPageOptionEllipsis" : ""}`}
              onClick={() => page !== "..." && props.onPageChange(page)}
            >
              {page}
            </div>
          ))}
          <button
            type="button"
            className="PaginationNavButton"
            onClick={() =>
              props.currentPage < props.totalPages &&
              props.onPageChange(props.currentPage + 1)
            }
            disabled={
              props.currentPage >= props.totalPages || props.totalPages <= 0
            }
            aria-label="Next page"
          >
            <img alt="" src="/media/icons/chevronRight.svg" />
          </button>
        </div>

        <div className="PaginationGoTo">
          <div className="PaginationLabel">Go to:</div>
          <input
            type="text"
            inputMode="numeric"
            className={`PaginationGoToInput${
              goToError ? " PaginationGoToInputError" : ""
            }`}
            value={goToValue}
            onChange={handleGoToChange}
            onKeyDown={handleGoToKeyDown}
            aria-invalid={goToError}
          />
          <button
            type="button"
            className="PaginationGoToButton"
            onClick={handleGoToSubmit}
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

export default injectIntl(Pagination);
